// ============================================================
// Wauvea WebXR — Main Entry Point
// Boot sequence: renderer → scene → dome → screen → zones → XR
// ============================================================

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

import { CONFIG } from './config.js';
import { DomeEnvironment } from './scene/DomeEnvironment.js';
import { MainScreen } from './scene/MainScreen.js';
import { ZoneManager } from './scene/ZoneManager.js';
import { XRSession as XRSessionManager } from './xr/XRSession.js';
import { XRControllers } from './xr/XRControllers.js';
import { HUD } from './ui/HUD.js';
import { VideoControls } from './ui/VideoControls.js';
import { VROverlay } from './ui/VROverlay.js';
import { LoadingScreen } from './ui/LoadingScreen.js';
// CalibrationPanel removed — controlled by Projection Room via postMessage

// ===== Globals =====
let renderer, scene, camera, controls;
let dome, mainScreen, zoneManager;
let xrSession, xrControllers;
let hud, videoControls, vrOverlay, loadingScreen;
let raycaster, mouse;

// Home camera position (saved for preview restore)
let homePosition = { x: 0, y: 5, z: 0.1 };
let homeTarget = { x: 0, y: 5, z: 0 };

// FPS fly-mode state
let panModeActive = false;
const keys = {};
let flyYaw = 0, flyPitch = 0;
let isDraggingMouse = false;
const FLY_SPEED = 0.15;
const MOUSE_SENS = 0.003;

// Preview mode — embedded in Projection Room iframe
const isPreviewMode = new URLSearchParams(window.location.search).get('preview') === 'true';

// ─── Adaptive Quality (inspired by A-Frame's stable approach) ───
// Instead of hiding zones/glow (causes black patches), we reduce material
// complexity during fast turns, keeping everything visible.
let adaptiveResEnabled = true;          // Toggle from Projection Room config
const ARS_ROT_THRESHOLD = 0.015;        // Quaternion delta that triggers quality reduction
let _prevCamQuat = null;                // Previous frame camera quaternion
let _isReducedQuality = false;          // Whether we're in reduced quality mode
let _settledFrames = 0;                 // Frames since last fast turn
const ARS_SETTLE_FRAMES = 10;           // Frames of calm before restoring quality
let _arsLogCounter = 0;                 // Throttle debug logging

// ─── Remote Debug Logger ───
// Sends console output to the Vite dev server so you can monitor AVP from the terminal
// Only active on localhost — disabled in production to avoid 404 spam
(function setupRemoteLogger() {
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  if (!isLocal) return; // Skip on Netlify / production — no /debug-log endpoint

  const endpoint = '/debug-log';
  const _origLog = console.log;
  const _origWarn = console.warn;
  const _origError = console.error;
  const _origInfo = console.info;

  function send(level, args) {
    try {
      const serialized = args.map(a => {
        if (a instanceof Error) return a.message;
        if (typeof a === 'object') try { return JSON.stringify(a); } catch { return String(a); }
        return String(a);
      });
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ level, args: serialized, ts: Date.now() }),
      }).catch(() => {}); // Ignore network errors
    } catch (_e) { /* bail silently */ }
  }

  console.log = (...a) => { _origLog(...a); send('log', a); };
  console.warn = (...a) => { _origWarn(...a); send('warn', a); };
  console.error = (...a) => { _origError(...a); send('error', a); };
  console.info = (...a) => { _origInfo(...a); send('info', a); };
})();

// ===== Init =====
async function init() {
  loadingScreen = new LoadingScreen();
  if (isPreviewMode) loadingScreen.hide(); // Skip loading screen in preview
  loadingScreen.update(5, 'Creating renderer...');

  // Detect Apple Vision Pro (visionOS Safari)
  const isVisionPro = /Apple/.test(navigator.vendor) && /Safari/.test(navigator.userAgent) && 'xr' in navigator;
  console.log('[Init] Platform:', navigator.userAgent.slice(0, 80));
  console.log('[Init] AVP detected:', isVisionPro, '| antialias:', !isVisionPro);
  console.log('[Init] Preview mode:', isPreviewMode, '| Adaptive res:', adaptiveResEnabled);

  renderer = new THREE.WebGLRenderer({
    antialias: !isVisionPro,  // MSAA breaks WebXR on visionOS — causes black screen
    alpha: true,              // Match A-Frame: alpha canvas for compositor blending
    stencil: false,           // Match A-Frame: disable stencil buffer (saves VRAM)
    powerPreference: 'high-performance',
  });
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.xr.enabled = true;

  // ─── A-Frame-inspired sort: front-to-back for opaques (minimizes overdraw) ───
  renderer.setOpaqueSort((a, b) => {
    if (a.groupOrder !== b.groupOrder) return a.groupOrder - b.groupOrder;
    if (a.renderOrder !== b.renderOrder) return a.renderOrder - b.renderOrder;
    return a.z - b.z;
  });

  // ─── XR Performance: A-Frame uses foveation=1 for max perf ───
  // We use 0.5 for good balance between quality and performance
  renderer.xr.setFramebufferScaleFactor(1.0);
  renderer.xr.setFoveation(0.5);
  renderer.xr.addEventListener('sessionstart', () => {
    renderer.xr.setFoveation(0.5);
    console.log('[WebXR] Session started — foveation: 0.5 (balanced)');
    // Target 90Hz on Quest (A-Frame does this automatically)
    const session = renderer.xr.getSession();
    if (session?.supportedFrameRates && session.updateTargetFrameRate) {
      const rates = session.supportedFrameRates;
      const target = rates.includes(90) ? 90 : (rates.includes(72) ? 72 : 60);
      session.updateTargetFrameRate(target).then(() => {
        console.log('[WebXR] Frame rate target:', target + 'Hz');
      }).catch(e => console.warn('[WebXR] Frame rate set failed:', e));
    }
  });
  document.getElementById('canvas-container').appendChild(renderer.domElement);

  loadingScreen.update(10, 'Setting up scene...');

  // Scene
  scene = new THREE.Scene();
  scene.background = new THREE.Color(CONFIG.dome.ambientColor);

  // Camera
  camera = new THREE.PerspectiveCamera(
    CONFIG.camera.fov,
    window.innerWidth / window.innerHeight,
    CONFIG.camera.near,
    CONFIG.camera.far
  );
  camera.position.set(0, 5, 0.1);

  // Controls (desktop)
  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.06;
  controls.rotateSpeed = -0.3;
  controls.enableZoom = true;
  controls.minDistance = 0.1;
  controls.maxDistance = 5;
  controls.enablePan = false;
  controls.target.set(0, 5, 0);
  controls.maxPolarAngle = Math.PI * 0.85;
  controls.minPolarAngle = Math.PI * 0.15;

  // Raycaster
  raycaster = new THREE.Raycaster();
  mouse = new THREE.Vector2();

  // ===== Build Environment =====
  loadingScreen.update(15, 'Building environment...');
  dome = new DomeEnvironment(scene);
  await dome.build('minimalist');

  loadingScreen.update(40, 'Building main stage...');
  mainScreen = new MainScreen(scene);
  mainScreen.build(); // Media assigned from Projection Room via drag-and-drop

  loadingScreen.update(60, 'Creating zones...');
  zoneManager = new ZoneManager(scene);
  await zoneManager.build();

  loadingScreen.update(80, 'Setting up controls...');

  // ===== UI =====
  hud = new HUD();
  videoControls = new VideoControls(mainScreen);
  vrOverlay = new VROverlay(scene, renderer, mainScreen);

  // Calibration is now controlled by the Projection Room via postMessage
  // No in-scene calibration panel needed

  // ===== WebXR =====
  loadingScreen.update(90, 'Checking WebXR...');
  xrSession = new XRSessionManager(renderer);
  const vrSupported = await xrSession.isSupported();
  console.log('[XR] VR supported:', vrSupported);

  if (vrSupported) {
    xrControllers = new XRControllers(renderer, scene);
    xrControllers.build();

    hud.showVRButton();
    hud.setVRStatus('VR Ready');

    // VR button handler
    document.getElementById('btn-enter-vr')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      try {
        await xrSession.toggle();
      } catch (err) {
        hud.setVRStatus('VR failed: ' + err.message);
      }
    });

    xrSession.onSessionStart = () => {
      console.log('[XR] ✅ Session STARTED');
      hud.setVRActive(true);
      mainScreen.play();
      // A-Frame insight: disable tone mapping in XR (expensive post-process)
      renderer._savedToneMapping = renderer.toneMapping;
      renderer.toneMapping = THREE.NoToneMapping;
      // Show 3D VR controls
      console.log('[XR] VR overlay shown, adaptive res:', adaptiveResEnabled);
      vrOverlay.show();
    };

    xrSession.onSessionEnd = () => {
      console.log('[XR] ❌ Session ENDED');
      hud.setVRActive(false);
      if (renderer._savedToneMapping !== undefined) {
        renderer.toneMapping = renderer._savedToneMapping;
      }
      vrOverlay.hide();
    };

    // Debounce to prevent double-select (controller + session both fire on AVP)
    let _lastSelectTime = 0;
    function handleXRSelect(rc) {
      const now = performance.now();
      if (now - _lastSelectTime < 200) return; // Skip duplicate within 200ms
      _lastSelectTime = now;

      // Try VR overlay controls first
      if (vrOverlay.handleSelect(rc)) return;

      // Fall through to zone click
      const hits = rc.intersectObjects(zoneManager.clickableMeshes, false);
      if (hits.length > 0 && hits[0].object.userData.isClickable) {
        hits[0].object.material.emissiveIntensity = 0.8;
        setTimeout(() => {
          hits[0].object.material.emissiveIntensity = hits[0].object.userData.baseEmissiveIntensity;
        }, 200);
      }
    }

    // XR controller select (Quest controllers, etc.)
    xrControllers.onSelect = (controller) => {
      const tempMatrix = new THREE.Matrix4();
      tempMatrix.identity().extractRotation(controller.matrixWorld);
      const rc = new THREE.Raycaster();
      rc.ray.origin.setFromMatrixPosition(controller.matrixWorld);
      rc.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix);
      handleXRSelect(rc);
    };

    // AVP hand tracking: session-level 'select' on pinch
    renderer.xr.addEventListener('sessionstart', () => {
      const session = renderer.xr.getSession();
      if (!session) return;
      session.addEventListener('select', (event) => {
        console.log('[XR] SELECT from', event.inputSource?.handedness || 'unknown');
        const inputSource = event.inputSource;
        if (!inputSource?.targetRaySpace) return;

        const refSpace = renderer.xr.getReferenceSpace();
        const frame = event.frame;
        if (!frame || !refSpace) return;

        const pose = frame.getPose(inputSource.targetRaySpace, refSpace);
        if (!pose) return;

        const rc = new THREE.Raycaster();
        rc.ray.origin.set(pose.transform.position.x, pose.transform.position.y, pose.transform.position.z);
        const dir = new THREE.Vector3(0, 0, -1);
        dir.applyQuaternion(new THREE.Quaternion(
          pose.transform.orientation.x, pose.transform.orientation.y,
          pose.transform.orientation.z, pose.transform.orientation.w
        ));
        rc.ray.direction.copy(dir);
        handleXRSelect(rc);
      });
      console.log('[XR] Session-level select listener registered');
    });
  } else {
    hud.setVRStatus(navigator.xr ? 'VR not available' : 'WebXR not supported');
  }

  // ===== Events =====
  setupEvents();

  // ===== Preview Mode — listen for postMessage from Projection Room =====
  if (isPreviewMode) {
    setupPreviewMessageListener();
    // Hide HUD chrome in preview — the Projection Room is the UI
    document.getElementById('hud-overlay')?.classList.add('preview-mode');
    // Notify parent that preview is ready
    window.parent.postMessage({ type: 'previewReady' }, '*');
    console.log('[WebXR] previewReady sent to parent');
  } else {
    // ===== Standalone Mode — load saved settings from URL hash or localStorage =====
    let settingsJson = null;
    // Check URL hash first (passed by "Open in new tab" button)
    const hash = window.location.hash;
    if (hash.startsWith('#config=')) {
      try {
        const encoded = hash.replace('#config=', '');
        settingsJson = JSON.parse(decodeURIComponent(escape(atob(encoded))));
        console.log('[WebXR] Loaded settings from URL hash');
        // Clean the URL hash so it doesn't persist on refresh
        history.replaceState(null, '', window.location.pathname);
      } catch (e) { console.warn('[WebXR] Failed to parse URL hash config:', e); }
    }
    // Fall back to localStorage
    if (!settingsJson) {
      try {
        const raw = localStorage.getItem('wauvea-projection-room-settings');
        if (raw) { settingsJson = JSON.parse(raw); console.log('[WebXR] Loaded settings from localStorage'); }
      } catch (e) { /* ignore */ }
    }
    if (settingsJson) applySettings(settingsJson);
  }

  loadingScreen.update(100, 'Ready!');
  loadingScreen.hide();
  hud.show();
  videoControls.show();

  // Start render loop
  renderer.setAnimationLoop(animate);
}

// ===== Event Listeners =====
function setupEvents() {
  // Resize
  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });

  // Mouse hover
  renderer.domElement.addEventListener('mousemove', (e) => {
    mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;

    raycaster.setFromCamera(mouse, camera);
    const hit = zoneManager.checkHover(raycaster);

    if (hit) {
      hud.showTooltip(hit.zone.config.label);
    } else {
      hud.hideTooltip();
    }
  });

  // Click on zone — notify parent
  renderer.domElement.addEventListener('click', (e) => {
    mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;

    raycaster.setFromCamera(mouse, camera);

    const intersects = raycaster.intersectObjects(zoneManager.clickableMeshes, false);
    if (intersects.length > 0) {
      const panel = intersects[0].object;
      if (panel.userData.isClickable && panel.userData.zoneId) {
        panel.material.emissiveIntensity = 0.8;
        setTimeout(() => {
          panel.material.emissiveIntensity = panel.userData.baseEmissiveIntensity;
        }, 200);
        // Notify parent about zone selection
        if (isPreviewMode) {
          window.parent.postMessage({ type: 'zoneSelected', payload: { zoneId: panel.userData.zoneId } }, '*');
        }
      }
    }
  });

  // ===== FPS Fly Mode Listeners =====
  window.addEventListener('keydown', (e) => {
    keys[e.key.toLowerCase()] = true;
  });
  window.addEventListener('keyup', (e) => {
    keys[e.key.toLowerCase()] = false;
  });

  // Any mouse drag to look around in fly mode
  renderer.domElement.addEventListener('mousedown', (e) => {
    if (panModeActive) {
      isDraggingMouse = true;
      e.preventDefault();
    }
  });
  window.addEventListener('mouseup', () => {
    isDraggingMouse = false;
  });
  renderer.domElement.addEventListener('contextmenu', (e) => {
    if (panModeActive) e.preventDefault();
  });
  renderer.domElement.addEventListener('mousemove', (e) => {
    if (!panModeActive || !isDraggingMouse) return;
    flyYaw -= e.movementX * MOUSE_SENS;
    flyPitch -= e.movementY * MOUSE_SENS;
    flyPitch = THREE.MathUtils.clamp(flyPitch, -Math.PI / 2 + 0.1, Math.PI / 2 - 0.1);

    // Apply rotation to camera
    const quat = new THREE.Quaternion();
    const euler = new THREE.Euler(flyPitch, flyYaw, 0, 'YXZ');
    camera.quaternion.setFromEuler(euler);
  });

  // Scroll wheel to fly forward/back
  renderer.domElement.addEventListener('wheel', (e) => {
    if (!panModeActive) return;
    e.preventDefault();
    const dir = new THREE.Vector3();
    camera.getWorldDirection(dir);
    camera.position.addScaledVector(dir, -e.deltaY * 0.01);
  }, { passive: false });
}

// ===== Render Loop =====
let _lastTimeUpdate = 0;
function animate() {
  if (!xrSession?.isActive) {
    if (panModeActive) {
      updateFlyControls();
    } else {
      controls.update();
    }
  }
  // Only check hover in XR when controllers are active (skip per-frame cost otherwise)
  if (xrSession?.isActive && xrControllers) {
    // Combine zone meshes + VR overlay clickables for ray blocking
    const allClickables = [...zoneManager.clickableMeshes, ...vrOverlay.clickables];
    xrControllers.checkHover(allClickables);
  }

  // ─── Adaptive Quality in XR (A-Frame-inspired: never hide, just simplify) ───
  // A-Frame doesn't hide zones/glow during fast turns and performs fine.
  // Key insight: the black patches were CAUSED by our old ARS hiding things.
  // New approach: everything stays visible. We only log for diagnostics.
  if (xrSession?.isActive && adaptiveResEnabled) {
    const xrCam = renderer.xr.getCamera();
    if (xrCam) {
      const q = xrCam.quaternion;
      if (_prevCamQuat) {
        const dot = Math.abs(_prevCamQuat.x * q.x + _prevCamQuat.y * q.y + _prevCamQuat.z * q.z + _prevCamQuat.w * q.w);
        const rotDelta = 1.0 - dot;

        if (rotDelta > ARS_ROT_THRESHOLD) {
          _settledFrames = 0;
          if (!_isReducedQuality) {
            _isReducedQuality = true;
            // Don't hide anything — just log
            if (++_arsLogCounter % 60 === 0) {
              console.log('[ARS] ⚡ Fast turn detected (delta:', rotDelta.toFixed(4) + ')');
            }
          }
        } else {
          _settledFrames++;
          if (_isReducedQuality && _settledFrames > ARS_SETTLE_FRAMES) {
            _isReducedQuality = false;
          }
        }
      }
      if (!_prevCamQuat) _prevCamQuat = new THREE.Quaternion();
      _prevCamQuat.copy(q);
    }
  }

  mainScreen.update();
  zoneManager.update();
  if (xrSession?.isActive) vrOverlay.update();
  renderer.render(scene, camera);

  // Send timeUpdate to parent — throttled to every ~500ms
  if (isPreviewMode && mainScreen.videoElement) {
    const now = performance.now();
    if (now - _lastTimeUpdate > 500) {
      _lastTimeUpdate = now;
      window.parent.postMessage({
        type: 'timeUpdate',
        payload: {
          currentTime: mainScreen.currentTime,
          duration: mainScreen.duration,
          playing: mainScreen.isPlaying,
        },
      }, '*');
    }
  }
}

// ===== FPS Fly Controls =====
function updateFlyControls() {
  const dir = new THREE.Vector3();
  camera.getWorldDirection(dir);
  const right = new THREE.Vector3().crossVectors(dir, camera.up).normalize();

  if (keys['w'] || keys['arrowup'])    { camera.position.addScaledVector(dir, FLY_SPEED); }
  if (keys['s'] || keys['arrowdown'])  { camera.position.addScaledVector(dir, -FLY_SPEED); }
  if (keys['a'] || keys['arrowleft'])  { camera.position.addScaledVector(right, -FLY_SPEED); }
  if (keys['d'] || keys['arrowright']) { camera.position.addScaledVector(right, FLY_SPEED); }
  if (keys['e'] || keys[' '])          { camera.position.y += FLY_SPEED; }
  if (keys['q'] || keys['shift'])      { camera.position.y -= FLY_SPEED; }
}

function enterFlyMode() {
  panModeActive = true;
  homePosition = { x: camera.position.x, y: camera.position.y, z: camera.position.z };
  homeTarget = { x: controls.target.x, y: controls.target.y, z: controls.target.z };
  controls.enabled = false;

  // Calculate initial yaw/pitch from current camera direction
  const dir = new THREE.Vector3();
  camera.getWorldDirection(dir);
  flyYaw = Math.atan2(dir.x, dir.z);
  flyPitch = Math.asin(THREE.MathUtils.clamp(dir.y, -1, 1));
}

function exitFlyMode() {
  panModeActive = false;
  controls.enabled = true;
  camera.position.set(homePosition.x, homePosition.y, homePosition.z);
  controls.target.set(homeTarget.x, homeTarget.y, homeTarget.z);
  controls.update();
}

// ===== Preview Mode — PostMessage Handler =====
function setupPreviewMessageListener() {
  window.addEventListener('message', (event) => {
    const { type, payload } = event.data || {};
    if (!type) return;

    switch (type) {
      case 'updateZone':
        // payload: { zoneId, media?: { monoFile, kind, title }, adCreative?: { image, title } }
        if (payload.media || payload.adCreative) {
          let url = payload.media?.monoFile || payload.adCreative?.image;
          // Handle monoFile as object { s3Key, ... } or string
          if (url && typeof url === 'object' && url.s3Key) {
            url = url.s3Key; // Will be relative — won't load, but at least won't crash
          }
          const kind = payload.media?.kind || 'image';
          console.log(`[WebXR] updateZone: ${payload.zoneId} → url=${url}, kind=${kind}`);
          if (url) {
            zoneManager.setZoneMedia(payload.zoneId, url, kind);
          }
        }
        break;

      case 'clearZone':
        zoneManager.clearZoneMedia(payload.zoneId);
        break;

      case 'updateScreen':
        // payload: { projection, arcDegrees, radius, height, screenArc }
        mainScreen.rebuild(scene, payload);
        break;

      case 'updateEnvironment':
        // payload: { environmentType: 'full' | 'minimalist' }
        // Minimalist = hide overhead, Full = show overhead
        // Does NOT change skybox — that's updateSkyboxColor
        if (zoneManager) {
          const envType = payload.environmentType || 'minimalist';
          const overheadZone = zoneManager.getZoneById('overhead');
          if (overheadZone && overheadZone.mesh) {
            overheadZone.mesh.visible = (envType === 'full' || envType === 'spherical');
          }
          console.log('[WebXR] Environment:', envType, '→ overhead:', envType === 'full' || envType === 'spherical' ? 'visible' : 'hidden');
        }
        break;

      case 'updateViewer':
        // payload: { y, z }
        controls.target.set(0, payload.y, payload.z);
        camera.position.set(0, payload.y, payload.z + 0.1);
        controls.update();
        // Keep home position in sync
        homePosition = { x: 0, y: payload.y, z: payload.z + 0.1 };
        homeTarget = { x: 0, y: payload.y, z: payload.z };
        break;

      case 'updateZoneTransform':
        // payload: { zoneId, x, y, z, width, height }
        zoneManager.setZoneTransform(payload.zoneId, payload);
        break;

      case 'updateMainVideo':
        // payload: { url }
        console.log('[WebXR] updateMainVideo received, url:', payload.url);
        mainScreen.setVideoSource(payload.url);
        break;

      case 'updateSkyboxColor':
        // payload: { color } — hex string like '#0a0a1a'
        if (dome && dome.skyMesh) {
          dome.skyMesh.material.color.set(payload.color);
          dome.skyMesh.material.needsUpdate = true;
        }
        scene.background = new THREE.Color(payload.color);
        break;

      case 'updateSkyboxScale': {
        // payload: { scale } — 0.3 to 2.0 (1.0 = default)
        const scale = payload?.scale || 1;
        if (dome && dome.skyMesh) {
          dome.skyMesh.scale.setScalar(scale);
        }
        // Also scale ground if present
        const groundObj = scene.getObjectByName('ground');
        if (groundObj) {
          groundObj.scale.setScalar(scale);
        }
        console.log('[WebXR] Skybox scale:', scale);
        break;
      }

      case 'setSceneMode':
        // payload: { mode: 'edit' | 'preview' }
        zoneManager.setEditMode(payload.mode === 'edit');
        // Main screen: double-sided in edit, back-side only in preview
        if (mainScreen.mesh) {
          mainScreen.mesh.material.side = payload.mode === 'edit' ? THREE.DoubleSide : THREE.BackSide;
          mainScreen.mesh.material.needsUpdate = true;
        }
        if (payload.mode === 'preview') {
          panModeActive = false;
          controls.enabled = true;
          camera.position.set(homePosition.x, homePosition.y, homePosition.z);
          controls.target.set(homeTarget.x, homeTarget.y, homeTarget.z);
          controls.enablePan = false;
          controls.maxDistance = 5;
          controls.minDistance = 0.1;
          controls.rotateSpeed = -0.3;
          controls.maxPolarAngle = Math.PI * 0.85;
          controls.minPolarAngle = Math.PI * 0.15;
          controls.update();
        }
        break;

      case 'setPanMode':
        // payload: { enabled: boolean }
        if (payload.enabled) {
          enterFlyMode();
        } else {
          exitFlyMode();
        }
        break;

      case 'dropOnZone':
        // payload: { x, y, media?, adCreative? } — normalised coords
        raycaster.setFromCamera(new THREE.Vector2(payload.x, payload.y), camera);
        const hits = raycaster.intersectObjects(zoneManager.clickableMeshes, false);
        if (hits.length > 0 && hits[0].object.userData.zoneId) {
          const zoneId = hits[0].object.userData.zoneId;
          const url = payload.media?.monoFile || payload.adCreative?.image;
          const kind = payload.media?.kind || 'image';
          if (url) zoneManager.setZoneMedia(zoneId, url, kind);
          window.parent.postMessage({ type: 'zoneDropped', payload: { zoneId } }, '*');
        } else {
          // Dropped on main screen area
          const mainHits = raycaster.intersectObject(mainScreen.mesh, false);
          if (mainHits.length > 0) {
            const url = payload.media?.monoFile || payload.adCreative?.image;
            if (url && payload.media?.kind === 'video') mainScreen.setVideoSource(url);
            window.parent.postMessage({ type: 'zoneDropped', payload: { zoneId: 'main-screen' } }, '*');
          }
        }
        break;

      case 'play':
        mainScreen.play();
        break;

      case 'pause':
        mainScreen.pause();
        break;

      case 'seek':
        // payload: { time }
        mainScreen.seek(payload.time);
        break;

      case 'updateCornerRadius':
        // payload: { zoneId: string | 'all', radius: number }
        zoneManager.setZoneCornerRadius(payload.zoneId, payload.radius);
        break;

      case 'updateMainScreenCornerRadius':
        // payload: { radius: number } — 0 = sharp, 1–10 = graduated
        mainScreen.setCornerRadius(payload.radius);
        break;

      case 'updateAmbientGlow':
        // payload: { color: string, intensity: number }
        mainScreen.setAmbientGlow(payload.color, payload.intensity);
        break;

      case 'enableDynamicGlow':
        // payload: { enabled: boolean }
        mainScreen.enableDynamicGlow(payload.enabled);
        break;

      case 'updateZoneColor':
        // payload: { zoneId: string, color: string }
        zoneManager.setZoneColor(payload.zoneId, payload.color);
        break;

      case 'updateMainScreenColor':
        // payload: { color: string }
        mainScreen.setMainScreenColor(payload.color);
        break;

      case 'keydown':
        keys[payload.key] = true;
        break;

      case 'keyup':
        keys[payload.key] = false;
        break;

      case 'setAdaptiveRes':
        // payload: { enabled: boolean }
        adaptiveResEnabled = !!payload.enabled;
        if (!adaptiveResEnabled) {
          // Reset to full quality when disabled
          _currentScale = ARS_HIGH;
          renderer.xr.setFramebufferScaleFactor(ARS_HIGH);
        }
        console.log('[WebXR] Adaptive resolution:', adaptiveResEnabled ? 'ON' : 'OFF');
        break;
    }
  });

  // Tell parent we're ready
  window.parent.postMessage({ type: 'previewReady' }, '*');
}

// ===== Apply saved settings to scene (standalone mode) =====
function applySettings(s) {
  try {
    console.log('[WebXR] Applying saved settings...');

    // Screen (new fields with backward compat)
    const sWidth = s.screenWidth ?? s.arcDegrees ?? 190;
    const sHeight = s.screenHeight ?? 18;
    const sArc = s.screenArc != null ? s.screenArc : (s.projection === 'flat' ? 0 : 180);
    mainScreen.rebuild(scene, {
      arcDegrees: sWidth,
      radius: 30,
      height: sHeight,
      screenArc: sArc,
    });

    // Environment — toggle overhead
    if (s.envType && zoneManager) {
      const envType = s.envType === 'spherical' ? 'full' : s.envType;
      const overheadZone = zoneManager.getZoneById('overhead');
      if (overheadZone && overheadZone.mesh) {
        overheadZone.mesh.visible = envType === 'full';
      }
    }

    // Skybox
    if (s.skyboxColor) {
      scene.background = new THREE.Color(s.skyboxColor);
    }

    // Viewer
    if (s.viewerY != null || s.viewerZ != null) {
      camera.position.set(0, s.viewerY ?? 5, s.viewerZ ?? 0.1);
      controls.target.set(0, s.viewerY ?? 5, 0);
      controls.update();
    }

    // Main screen corner radius
    if (s.mainScreenCornerRadius > 0) {
      mainScreen.setCornerRadius(s.mainScreenCornerRadius);
    }

    // Ambient glow
    if (s.glowIntensity > 0) {
      mainScreen.setAmbientGlow(s.glowColor || '#6C5CE7', s.glowIntensity);
      if (s.glowMode === 'auto') {
        mainScreen.enableDynamicGlow(true);
      }
    }

    // Adaptive resolution
    if (s.adaptiveRes !== undefined) {
      adaptiveResEnabled = !!s.adaptiveRes;
      console.log('[WebXR] Adaptive resolution from config:', adaptiveResEnabled ? 'ON' : 'OFF');
    }

    // Zone transforms
    if (s.zoneTransforms) {
      Object.entries(s.zoneTransforms).forEach(([id, t]) => {
        zoneManager.setZoneTransform(id, t);
      });
    }

    // Zone colors
    if (s.zoneColors) {
      Object.entries(s.zoneColors).forEach(([id, color]) => {
        zoneManager.setZoneColor(id, color);
      });
    }

    // Zone corner radius
    if (s.cornerRadius > 0) {
      zoneManager.setZoneCornerRadius('all', s.cornerRadius);
    }

    // Main screen video
    const mainMedia = s.zoneAssignments?.['main-screen']?.media;
    if (mainMedia?.monoFile) {
      const key = mainMedia.monoFile.s3Key || mainMedia.monoFile;
      if (typeof key === 'string') {
        const encoded = key.split('/').map(seg => encodeURIComponent(seg)).join('/');
        const url = `/s3-media/${encoded}`;
        mainScreen.setVideoSource(url);
      }
    }

    // Zone media
    if (s.zoneAssignments) {
      Object.entries(s.zoneAssignments).forEach(([zoneId, a]) => {
        if (zoneId === 'main-screen') return;
        const assignment = a;
        if (assignment.media?.monoFile) {
          const key = assignment.media.monoFile.s3Key || assignment.media.monoFile;
          if (typeof key === 'string') {
            const encoded = key.split('/').map(seg => encodeURIComponent(seg)).join('/');
            zoneManager.setZoneMedia(zoneId, `/s3-media/${encoded}`, assignment.media.kind || 'image');
          }
        }
      });
    }
  } catch (e) {
    console.warn('[WebXR] Failed to load saved settings:', e);
  }
}

// ===== Boot =====
init().catch((err) => {
  console.error('[Wauvea] Init error:', err);
  const status = document.getElementById('loader-status');
  if (status) status.textContent = 'Error loading. Check console.';
});
